import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { ClickAwayListener } from "@fidget/common/components/form/clickAwayListener";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { useStore } from "../../../models";
import { IInboxFilter } from "../../../models/inbox/InboxFilter";
import { UnreadType } from "../../../models/inbox/InboxFilter";
import styles from "./expandedFilters.css";
import { AssigneeFilter } from "./globalFilters/assigneeFilter";
import { SnoozedFilter } from "./globalFilters/snoozedFilter";
import { TagFilter } from "./globalFilters/tagFilter";
import { ThreadClosedFilter } from "./globalFilters/threadClosedFilter";
import { UnsnoozedFilter } from "./globalFilters/unsnoozedFilter";
import { YoursNewFilter } from "./globalFilters/yoursNewFilter";
import { TagGroupFilter } from "./globalFilters/tagGroupFilter";
interface Props {
  filters: Array<IInboxFilter>;
  onSelectFilter: (id: string) => void;
}

export const ExpandedFilters = observer(({ filters, onSelectFilter }: Props) => {
  const { inboxStore, uiStore } = useStore();

  const [isExpanded, setIsExpanded] = useState(false);

  const resetState = () => {
    setIsExpanded(false);
  };

  const handleFilterChosen = (filterId: string) => {
    onSelectFilter(filterId);
    resetState();
  };

  const handleClearGlobalClicked = () => {
    inboxStore.globalFilter.deactivateAll();
    resetState();
  };

  const renderButton = () => {
    const isSelected =
      inboxStore.globalFilter.isAnyFilterEnabled || filters.some((f) => f.isSelected);
    return (
      <TooltipComponent
        text={<L10n id={"inbox.moreFilters"} />}
        width={`60px`}
        maxWidth={`60px`}
        positionPreset={"bottom"}
      >
        <button
          className={classNames(styles.filter, { [styles.selected]: isSelected })}
          onClick={() => setIsExpanded(true)}
        >
          <div className={styles.dot} />
          <div className={styles.dot} />
          <div className={styles.dot} />
        </button>
      </TooltipComponent>
    );
  };

  const renderDropdown = () => (
    <div className={styles.dropdown}>
      {renderStatusFilters()}
      {uiStore.canDisplayAllConversations() ? (
        <>
          {renderYoursFilters()}
          {renderGlobalFilters()}
        </>
      ) : (
        renderYoursFilters(true)
      )}
    </div>
  );

  const renderStatusFilters = () => {
    return (
      <>
        <div className={styles.header}>
          <div>
            {uiStore.canDisplayAllConversations() && (
              <L10n id={"inbox.moreFilters.conversations"} />
            )}
          </div>
          <div className={styles.cross} onClick={() => setIsExpanded(false)}>
            <i className="icon-close-cross" />
          </div>
        </div>
        {uiStore.canDisplayAllConversations() && <div className={styles.line} />}
        {renderFilterRows()}
      </>
    );
  };

  const renderYoursFilters = (extended = false) => {
    return (
      <>
        {renderYoursHeader()}
        <div className={styles["row-container"]}>
          <YoursNewFilter resetState={resetState} />
          {!uiStore.canDisplayAllConversations() && <SnoozedFilter resetState={resetState} />}
          <UnsnoozedFilter resetState={resetState} />
          {extended && (
            <>
              <TagFilter resetState={resetState} />
              <TagGroupFilter resetState={resetState} />
            </>
          )}
        </div>
      </>
    );
  };

  const renderGlobalFilters = () => {
    return (
      <>
        {renderGlobalHeader()}
        <div className={styles["row-container"]}>
          <TagFilter resetState={resetState} />
          <TagGroupFilter resetState={resetState} />
          <AssigneeFilter resetState={resetState} />
          <ThreadClosedFilter resetState={resetState} />
          <SnoozedFilter resetState={resetState} />
        </div>
      </>
    );
  };

  const renderGlobalHeader = () => (
    <>
      <div className={styles.header}>
        <div>
          <L10n id={"inbox.moreFilters.allConversations"} />
        </div>
        <p className={styles["clear-filters"]} onClick={handleClearGlobalClicked}>
          {intl.formatMessage("inbox.moreFilters.clear")}
        </p>
      </div>
      <div className={styles.line} />
    </>
  );

  const renderYoursHeader = () => (
    <>
      <div className={styles.header}>
        <div>
          <L10n id={"inbox.moreFilters.yoursConversations"} />
        </div>
        <p className={styles["clear-filters"]} onClick={handleClearGlobalClicked}>
          {intl.formatMessage("inbox.moreFilters.clear")}
        </p>
      </div>
      <div className={styles.line} />
    </>
  );

  const renderFilterRows = () => (
    <div className={styles["row-container"]}>
      {filters.map((filter) => (
        <button
          key={filter.id}
          className={classNames(styles["entry-container"], {
            [styles.selected]: filter.isSelected
          })}
          onClick={() => handleFilterChosen(filter.id)}
          id={filter.id.replace(/\s+/g, "_") + "_filter"}
        >
          <span>
            {filter.unreadType === UnreadType.ENTRIES && !!filter.unreadCount && (
              <>
                {filter.unreadCount}
                <i className={classNames("icon-person", styles["icon-person"])} />
              </>
            )}
            {filter.label}
          </span>
          {filter.isSelected && renderAppliedText()}
        </button>
      ))}
    </div>
  );

  const renderAppliedText = () => (
    <div className={styles.applied}>
      <L10n id={"inbox.moreFilters.applied"} />
    </div>
  );

  const render = () => {
    return (
      <ClickAwayListener onClickAway={resetState}>
        {renderButton()}
        {isExpanded && renderDropdown()}
      </ClickAwayListener>
    );
  };

  return render();
});
